import {
  list,
  create,
  update,
  deleteChat,
  createConversationByMessage,
  getAiToolRecommendations,
  getSuggestionQuestions,
} from "@/api/chat";

// 聊天相关的 Vuex 模块
// 负责管理会话列表、当前会话、事件源示例、以及创建新会话等异步/同步操作
const chat = {
  namespaced: true,
  state: {
    showShare: false,
    // 会话列表，按时间或服务器返回顺序维护
    chatList: [],
    getChatListLoading: false,
    // 当前会话对象（来自 chatList 中的项）
    currentChat: {},
    // 当前会话的 id（用于快速定位 currentChat）
    currentChatId: "",
    // 存储 EventSource / SSE / fetch AbortController 等与流式响应相关的示例对象，key 为 conversationId
    eventSourceExample: {},
    // 未发送的消息缓存（当会话页面尚未准备好时，先保存消息，待页面就绪后再发送）
    // 结构: { [conversationId]: content }
    pendingMessages: {},
    // 会话链接内容的列表
    // 结构: { [conversationId]: list[] }
    chatLink: {},
    // 会话建议问题缓存，key 为 conversationId，value 为建议问题列表
    chatSuggestions: {},
    // 当前会话的建议问题列表
    applicationSuggestions: {},
    streamChatMap: {}, // 用于存储正在流式传输的会话内容，key 为 conversationId，value 消息
    // 可选问答模式列表，供 UI 展示与选择
    roleList: [
      {
        id: 1,
        icon: "normal",
        name: "通用模式",
        desc: "智能适配即时答案和其他模式",
      },
      {
        id: 2,
        icon: "swj",
        name: "税务局模式",
        desc: "将站在税务局角度分析、回答",
      },
      {
        id: 3,
        icon: "sws",
        name: "事务所模式",
        desc: "将站在事务所角度分析、回答",
      },
    ],
    // 内置代理（专家/助手）列表，用于快速切换聊天角色
    modelList: [
      {
        id: 1,
        name: "实训咨询专家",
        desc: "解答各类税务实务问题，提供专业处理建议和操作指南",
        icon: "sxzxzj",
        color: "134, 91, 239",
        hide: false,
         type: "sx" ,
      },
      {
        id: 2,
        name: "数据分析工程师",
        desc: "深度分析税务数据，识别风险并提供数据可视化报告",
        icon: "sjfxgcs",
        color: "11, 108, 238",
        hide: false, 
        type: "sjfx",
      },
      // {
      //   id: 3,
      //   name: "案例分析专家",
      //   desc: "分析历史税务判例，预测案件处理结果，提供",
      //   icon: "alfxzj",
      //   color: "255, 144, 0",
      //   hide: true, 
      // },
      // {
      //   id: 4,
      //   name: "成本造价评估专家",
      //   desc: "针对工程项目提供成本核算和税务影响评估",
      //   icon: "cbzjpgzj",
      //   color: "12, 191, 151",
      //   hide: true, 
      // },
      {
        id: 5,
        name: "国际税收",
        desc: "模型依靠国际税务知识进行训练",
        icon: "cbzjpgzj",
        color: "12, 191, 151",
        hide: true, 
        type: "gjss"
      },
    ],

    // 菜单更新触发标志
    menuUpdateFlag: 0,
    // 用户已添加的模型列表（边栏）
    userModelList: [],
  },
  mutations: {
    // 设置流式聊天内容
    setStreamChatContent(state, { id, content }) {
      state.streamChatMap = {
        ...state.streamChatMap,
        [id]: content,
      };
    },
    // 设置分享状态
    setShowShare(state, payload) {
      state.showShare = payload;
    },
    // 将从服务器获取到的会话列表写入 state
    setChatList(state, payload) {
      state.chatList = payload;
    },
    setGetChatListLoading(state, payload) {
      state.getChatListLoading = payload;
    },
    // 设置当前会话 id，并根据 id 从 chatList 中查找 currentChat
    setCurrentChatById(state, payload) {
      state.currentChatId = payload;
      const currentChat = state.chatList.find((item) => item.id == payload);
      state.currentChat = currentChat || {};
    },
    // 更新 currentChat 的部分字段（合并更新）
    updateCurrentChat(state, payload) {
      state.currentChat = {
        ...state.currentChat,
        ...payload,
      };
    },
    // 为指定会话设置 loading 标志，既更新 chatList 中对应项，也更新 currentChat（如果正在查看该会话）
    setChatLoading(state, { id, loading }) {
      if (!id) return;
      const idx = state.chatList.findIndex((i) => i.id == id);
      if (idx !== -1) {
        state.chatList[idx].loading = loading;
      }
      if (state.currentChatId == id) {
        state.currentChat = {
          ...state.currentChat,
          loading,
        };
      }
    },
    // 设置或清除 pendingMessages 中的某个会话缓存消息
    // 若 content 为 null 则清除缓存，否则设置为 content
    setPendingMessage(state, { id, content, fileLists }) {

      if (!id) return;
      if (content == null) {
        // 清除指定会话的待发送消息
        delete state.pendingMessages[id];
      } else {
        state.pendingMessages = {
          ...state.pendingMessages,
          [id]: { content, fileLists },
        };
      }
    },
    setChatLink(state, { id, list }) {
      if (!id) return;
      if (!list) {
        delete state.chatLink[id];
        return;
      } else {
        state.chatLink = {
          ...state.chatLink,
          [id]: list,
        };
      }
    },
    addChatToListhead: async (state, payload) => {
      state.chatList.unshift(payload);
    },
    setChatSuggestions(state, { id, list }) {
      if (!id) return;
      if (!list) {
        delete state.chatSuggestions[id];
        return;
      } else {
        state.chatSuggestions = {
          ...state.chatSuggestions,
          [id]: list,
        };
      }
    },
    setApplicationSuggestions(state, { id, applist }) {
      if (!id) return;
      if (!applist) {
        delete state.applicationSuggestions[id];
        return;
      } else {
        state.applicationSuggestions = {
          ...state.applicationSuggestions,
          [id]: applist,
        };
      }
    },
    // 更新 eventSourceExample 的映射（便于保存 AbortController / EventSource 等对象）
    setEventSourceExample(state, payload) {
      state.eventSourceExample = {
        ...state.eventSourceExample,
        ...payload,
      };
    },
    updateChatById(state, { id, ...payload }) {
      const idx = state.chatList.findIndex((i) => i.id == id);
      if (idx !== -1) {
        Object.keys(payload).forEach((key) => {
          state.chatList[idx][key] = payload[key];
        });
      }
    },
    // 设置探索更多的模型列表
    setmodelList(state, payload) {
      state.modelList = payload;
    },
    // 更新菜单触发标志
    updateMenuFlag(state) {
      state.menuUpdateFlag++;
    },
    // 设置用户已添加的模型列表
    setUserModelList(state, payload) {
      state.userModelList = payload;
    },
  },
  actions: {
    // 设置流式聊天内容
    setStreamChatContent({ commit }, { id, content }) {
      commit("setStreamChatContent", { id, content });
    },
    // 设置当前会话的 loading（UI 用）
    setCurrentLoading({ commit }, payload) {
      commit("updateCurrentChat", { loading: payload });
    },
    updateChatById({ commit }, payload) {
      commit("updateChatById", payload);
    },
    // 直接更新 currentChat（payload 应为部分字段）
    updateCurrentChat({ commit }, payload) {
      commit("updateCurrentChat", payload);
    },
    // 从后端获取会话列表并写入 state，会按 createTime 倒序排序
    getChatList: async ({ state, commit, rootState }) => {
      commit("setGetChatListLoading", true);
      const userId = rootState.user.userInfo.userId;
      const formData = new FormData();
      formData.append("userId", userId);
      const res = await list(formData);
      const conversationList = res
        .sort((a, b) => b.createTime - a.createTime)
        .map((item) => ({
          ...item,
          loading: false,
          edit: false,
          deleting: false,
        }));
      commit("setChatList", conversationList);
      if (state.currentChatId) {
        commit("setCurrentChatById", state.currentChatId);
      }
      commit("setGetChatListLoading", false);
    },
    // 直接设置会话列表（可用于本地修改后刷新）
    setChatList: ({ commit }, payload) => {
      commit("setChatList", payload);
    },
    // 设置当前会话（接收对象 { id } ）
    setCurrentChat: ({ commit }, payload) => {
      
      commit("setCurrentChatById", payload.id);
    },
    // 切换当前会话的代理（助手/专家）
    setCurrentMode: ({ commit }, payload) => {
      commit("updateCurrentChat", {
        modelId: payload.modelId,
      });
    },
    // 切换当前会话的模式
    setCurrentRole: ({ commit }, payload) => {
      commit("updateCurrentChat", {
        roleId: payload.roleId,
      });
    },
    updateChatTitle: async ({ commit }, payload) => {
      try {
        await update({
          id: payload.id,
          userId: payload.userId,
          title: payload.title,
        });
        if (payload.notLocal) return;
        commit("updateChatById", {
          id: payload.id,
          title: payload.title,
        });
      } catch (error) {
        throw error;
      }
    },
    // 创建新会话
    // payload 可能包含 { userId, modelId, content }
    // 创建成功后：
    // - 设置 currentChatId
    // - 将 currentChat 标记为 loading
    // - 若传入 content，则保存到 pendingMessages，等页面准备好再发送
    // - 更新 chatList 的 loading 状态并刷新列表
    createNewChat: async ({ commit, dispatch, rootState }, payload) => {
      try {
        const params = {
          title: payload.title || "未命名会话",
          userId: rootState.user.userInfo.userId,
          modelId: rootState.chat.currentChat.modelId || 1,
          roleId: rootState.chat.currentChat.roleId || 1,
        };
        const res = await create(params);
        // 将返回的 id 设为当前会话
        commit("setCurrentChatById", res);
        commit("updateCurrentChat", {
          id: res,
          loading: true,
        });
        // 若携带待发送内容，则保存，待 UI 就绪时发送
        if (payload && payload.content) {
          commit("setPendingMessage", {
            id: res,
            content: payload.content,
            fileLists: payload.fileLists,
          });
        }
        // 在会话列表中也显示 loading
        commit("setChatLoading", { id: res, loading: true });
        const listItem = {
          ...params,
          id: res,
          loading: true,
          edit: false,
          deleting: false,
          truncated: "",
          createTime: new Date().getTime(),
        };
        commit("addChatToListhead", listItem);
        // await dispatch("getChatList");
        return res;
      } catch (error) {
        commit("updateCurrentChat", {
          loading: true,
        });
        throw error;
      }
    },
    // 根据消息内容创建新会话（并返回会话 ID）
    createConversationByMessage: async ({ dispatch, rootState }, payload) => {
      try {
        const res = await createConversationByMessage({
          ...payload,
          userId: rootState.user.userInfo.userId,
          modelId: rootState.chat.currentChat.modelId || 1,
          roleId: rootState.chat.currentChat.roleId || 1,
        });
        await dispatch("getChatList");
        return res;
      } catch (error) {
        console.error("Error creating new chat by message:", error);
        throw error;
      }
    },
    // 删除指定会话并刷新列表
    deleteChat: async ({ dispatch, rootState }, payload) => {
      try {
        await deleteChat({
          id: payload.id,
          userId: rootState.user.userInfo.userId,
        });
        // 删除后刷新会话列表
        await dispatch("getChatList");
      } catch (error) {
        console.error("Error deleting chat:", error);
        throw error;
      }
    },
    // 将 loading 状态写入对应会话（包装 mutation）
    setChatLoading: ({ commit }, payload) => {
      commit("setChatLoading", payload);
    },
    // 包装 setPendingMessage mutation
    setPendingMessage: ({ commit }, payload) => {
      commit("setPendingMessage", payload);
    },
    // 包装 setChatLink mutation
    setChatLink: ({ commit }, payload) => {
      commit("setChatLink", payload);
    },
    getSuggestionQuestions: async (
      { commit, rootState, dispatch },
      payload
    ) => {
      const { id, content, model, modelId, fileLists } = payload;
      try {
        const params = {
          content: content,
          model: model,
          modelId: modelId,
          fileLists: fileLists || [],
        };
        const res = await getSuggestionQuestions(params);
        if (fileLists && fileLists.length > 0) {
          dispatch("getApplicationInfo", {
            id,
            fileLists: fileLists,
            messages: res.messages,
          });
        } else {
          commit("setChatSuggestions", {
            id,
            list: res.messages,
          });
        }
      } catch (error) {
        console.error("Error fetching suggestion questions:", error);
      }
    },
    getApplicationInfo: async ({ commit }, payload) => {
      const { id, messages, fileLists } = payload;
      try {
        const applist = await getAiToolRecommendations({ toolIds: messages });
        commit("setApplicationSuggestions", {
          id,
          applist: applist.map((app) => ({
            ...app,
            fileLists: fileLists,
          })),
        });
      } catch (error) {
        console.error("Error fetching application info:", error);
      }
    },
    // 包装 setEventSourceExample mutation（用于注册/更新 AbortController 或 EventSource）
    setEventSourceExample: ({ commit }, payload) => {
      commit("setEventSourceExample", payload);
    },
    // 停止当前会话的流式响应（如果存在对应的 AbortController / EventSource）并移除它
    stopCurrentStream: ({ state, commit }, payload) => {
      console.log("Stopping stream for chat ID:", payload);
      const currentId = payload.id;

      if (currentId && state.eventSourceExample[currentId]) {
        state.eventSourceExample[currentId].abort();
        // 中止后从映射中移除该示例
        const updatedEventSources = { ...state.eventSourceExample };
        delete updatedEventSources[currentId];
        commit("setEventSourceExample", updatedEventSources);
        commit("setChatLoading", { id: currentId, loading: false });
      }
    },
    // 触发菜单更新
    triggerMenuUpdate: ({ commit }) => {
      commit("updateMenuFlag");
    },
    // 设置用户已添加的模型列表
    setUserModelList: ({ commit }, payload) => {
      commit("setUserModelList", payload);
    },

  },
  getters: {},
};

export default chat;
